import { Facility } from '@reliance/types/models/Facility';
import React, { useCallback, useMemo } from 'react';
import type { SaltMinionFilters } from '../hooks/SaltMinion';
import type { SaltMinionListItem } from '../hooks/SaltMinion';
import { useSaltMinions } from '../hooks/SaltMinion';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import { useServerTableFilterState } from '../table/useServerTableFilterState';
import SaltMinionsTable from '../salt-minion/Table';
import { useAlerts } from '../alert/AlertContext';

type FacilitySaltMinionsTabProps = {
  facility: Facility;
};

const COLUMNS = [{ id: 'id' }, { id: 'actions' }];

const FacilitySaltMinionsTab: React.FC<FacilitySaltMinionsTabProps> = ({ facility }) => {
  const alerts = useAlerts();

  const baseRecord = useMemo(() => ({ facilityId: facility.id }), [facility.id]);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
  });

  const filters = useMemo((): SaltMinionFilters => tableState.mergedRecord as SaltMinionFilters, [tableState.mergedRecord]);

  const result = useSaltMinions(filters);

  useNotifyTabLoading('salt-minions', result.loading);

  React.useEffect(() => {
    if (result.error) {
      alerts.error('An error occurred while loading salt minions');
    }
  }, [result.error, alerts]);

  const onViewDetails = useCallback((minion: SaltMinionListItem) => {
    window.location.assign(`/salt-minions/view/${minion.id}`);
  }, []);

  const data = result.result;

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <SaltMinionsTable
          rows={data?.data ?? []}
          loading={result.loading}
          page={data?.page ?? tableState.pagination.pageIndex + 1}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          perPage={data?.perPage ?? tableState.pagination.pageSize}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
          onViewDetails={onViewDetails}
        />
      </div>
    </div>
  );
};

export default FacilitySaltMinionsTab;
